import { AnyAction } from 'redux'
import {
  startLoading,
  stopLoading,
  updateAppAlert,
  getErrorMessages,
  RequestFailureMessage,
  errorLogger,
  LoadingTypes,
} from '@labrav/common'
import { camelize } from 'casing'
import { isEmpty } from 'lodash'
import { AxiosError, AxiosResponse } from 'axios'
import FileDownload from 'js-file-download'

import { getReportsV2 } from '../../api/marketplace/index'
import { getAzureRevenueReports } from '../../api/azure/index'
import { getGcpRevenueReports } from '../../api/gcp/index'
import type { AppDispatch, RootState } from '../../store/types'
import type { GetRevenueInsightsReportV2 } from '../../types/index'
import { CloudMarketplace } from '../productsListing/types'
import { convertObjectKeysAndValuesToCommaSeprated } from '../../common/utils/revenueHelpers'
import {
  DateRange,
  FiltersState,
  LastEvaluatedObject,
  ReportsPayload,
  RevenueTrackingActionTypes,
} from './types'

export { RevenueTrackingActionTypes }

// `selectedInput` is the DB column/field name to filter on, coming from
// searchDropDownOptionMapping.revenueInsightsOptions ('buyer_company_name',
// 'aws_offer_id' or 'aws_account_id'). Maps it to the explicit query param
// getReportsV2 expects.
const getRevenueInsightsSearchParams = (
  input?: string,
  selectedInput?: string
) => {
  if (isEmpty(input)) {
    return {}
  }
  switch (selectedInput) {
    case 'buyer_company_name':
      return { companyName: input }
    case 'aws_offer_id':
      return { offerId: input }
    case 'aws_account_id':
      return { accountId: input }
    default:
      return {}
  }
}

const handleReportFetch = async ({
  partnerId,
  finalStartDate,
  finalEndDate,
  pageSize,
  productId,
  dateRangeType,
  lastEvaluatedKeys,
  lastEvaluatedValues,
  shouldSendGetDateParam,
  filters,
  input,
  selectedInput,
  pageNumber,
  cloudMarketplace,
}: {
  partnerId: string
  finalStartDate: string
  finalEndDate: string
  pageSize: number
  productId: string
  dateRangeType?: string
  lastEvaluatedKeys?: string | null
  lastEvaluatedValues?: string | null
  shouldSendGetDateParam: boolean
  filters: FiltersState
  input?: string
  selectedInput?: string
  pageNumber?: number
  cloudMarketplace: CloudMarketplace
}) => {
  switch (cloudMarketplace) {
    case 'AWS': {
      // Revenue insights search now queries the marketplace service directly
      // (DynamoDB-backed) instead of going through the OpenSearch based search
      // service.
      const { data } = await getReportsV2({
        partnerId,
        finalStartDate,
        finalEndDate,
        pageSize,
        productId,
        dateRangeType,
        lastEvaluatedKeys: lastEvaluatedKeys ?? undefined,
        lastEvaluatedValues: lastEvaluatedValues ?? undefined,
        shouldSendGetDateParam,
        ...filters,
        ...getRevenueInsightsSearchParams(input, selectedInput),
      })
      return data
    }
    case 'AZURE': {
      const { data } = await getAzureRevenueReports({
        partnerId,
        finalStartDate,
        finalEndDate,
        pageSize,
        pageNumber,
        productId,
      })
      return data
    }
    case 'GCP': {
      const { data } = await getGcpRevenueReports({
        partnerId,
        finalStartDate,
        finalEndDate,
        pageSize,
        pageNumber,
        productId,
      })
      const gcpData = data as {
        rows: unknown[]
        total_count: number
      }
      return {
        ...gcpData,
        records: gcpData.rows,
        page_size: pageSize,
        page_number: pageNumber || 1,
        count: gcpData.total_count,
      }
    }
  }
}

export const getRevenueInsights =
  ({
    downloadReport,
    partnerId,
    pageSizeValue,
    input,
    selectedInput,
    sectionName,
    pageNumber,
    lastEvaluatedObject,
    cloudMarketplace,
  }: {
    downloadReport: boolean
    partnerId: string
    pageSizeValue?: number
    input?: string
    selectedInput?: string
    sectionName?: string
    pageNumber?: number
    lastEvaluatedObject?: LastEvaluatedObject | null
    cloudMarketplace?: CloudMarketplace
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const { dateRange, filters, dateRangeType, insertDates } =
        getState().revenueInsightsV2
      const currentCloudMarketplace =
        cloudMarketplace || getState().productListingFilters.cloudMarketplace
      const productId =
        getState().productsListing[currentCloudMarketplace]?.selectedProduct ||
        ''
      const finalStartDate = dateRange.startDate
      const finalEndDate = dateRange.endDate
      const { pageSize: pageSizeStateValue } = getState().revenueInsightsV2
      const pageSize = pageSizeValue ? pageSizeValue : pageSizeStateValue
      const { keys: lastEvaluatedKeys, values: lastEvaluatedValues } =
        convertObjectKeysAndValuesToCommaSeprated(lastEvaluatedObject)
      const shouldSendGetDateParam =
        insertDates && Object.keys(insertDates).length > 0 ? false : true

      if (!downloadReport) {
        const data = await handleReportFetch({
          partnerId,
          finalStartDate,
          finalEndDate,
          pageSize,
          productId,
          dateRangeType,
          lastEvaluatedKeys,
          lastEvaluatedValues,
          shouldSendGetDateParam,
          filters,
          input,
          selectedInput,
          pageNumber,
          cloudMarketplace: currentCloudMarketplace,
        })
        await dispatch(
          setReportsData(
            camelize(data) as ReportsPayload,
            pageSize,
            currentCloudMarketplace
          )
        )
      } else {
        await downloadCSVForRevenueInsightsV2({
          partnerId,
          finalStartDate,
          finalEndDate,
          pageSize,
          productId,
          dateRangeType,
          lastEvaluatedKeys,
          lastEvaluatedValues,
          downloadReport,
          ...filters,
          input,
          selectedInput,
          pageNumber,
          sectionName,
        })
      }
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const setReportsData = (
  data: ReportsPayload,
  pageSize: number,
  cloudMarketplace: CloudMarketplace
) => ({
  type: RevenueTrackingActionTypes.SET_REPORTS_DATA,
  payload: { ...data, pageSize, cloudMarketplace },
})

export const downloadCSVForRevenueInsightsV2 = async ({
  partnerId,
  finalStartDate,
  finalEndDate,
  pageSize,
  productId,
  dateRangeType,
  paymentStatus,
  offerType,
  disbursementDate,
  paymentDueDate,
  paymentDelayedSince,
  lastEvaluatedKeys,
  lastEvaluatedValues,
  sortBy,
  downloadReport,
  sortOrder,
  input,
  selectedInput,
}: GetRevenueInsightsReportV2) => {
  // Revenue insights search now queries the marketplace service directly
  // (DynamoDB-backed) instead of going through the OpenSearch based search
  // service.
  return getReportsV2({
    partnerId,
    finalStartDate,
    finalEndDate,
    pageSize,
    productId,
    dateRangeType,
    paymentStatus,
    offerType,
    disbursementDate,
    paymentDueDate,
    paymentDelayedSince,
    lastEvaluatedKeys,
    lastEvaluatedValues,
    sortBy,
    downloadReport,
    sortOrder,
    ...getRevenueInsightsSearchParams(input, selectedInput),
  }).then(response => {
    FileDownload(response.data as Blob, `${finalStartDate}_${finalEndDate}.csv`)
  })
}

export const updateRevenuePageSize =
  (pageSize: number, partnerId: string) => async (dispatch: AppDispatch) => {
    await dispatch(setRevenuePageSize(pageSize))
    await dispatch(
      getRevenueInsights({
        downloadReport: false,
        partnerId,
      }) as unknown as AnyAction
    )
  }

export const setRevenuePageSize = (pageSize: number) => ({
  type: RevenueTrackingActionTypes.REVENUE_SET_LIMIT,
  payload: pageSize,
})

export const setRevenuePageNumber = (pageNumber: number) => ({
  type: RevenueTrackingActionTypes.REVENUE_SET_PAGE_NUMBER,
  payload: pageNumber,
})

export const clearRevenueReports = () => ({
  type: RevenueTrackingActionTypes.CLEAR_REVENUE_REPORTS,
})

export const setPageOneForRevenue = () => async (dispatch: AppDispatch) => {
  await dispatch(setRevenuePageNumber(1))
}

export const updateDateRange =
  (dates: DateRange) => async (dispatch: AppDispatch) => {
    const { startDate, endDate } = dates
    await dispatch(setDateRange({ startDate, endDate }))
    await dispatch(clearRevenueReports())
    await dispatch(setRevenuePageNumber(1))
  }

export const setDateRange = (dates: DateRange) => ({
  type: RevenueTrackingActionTypes.SET_DATE_RANGE,
  payload: dates,
})

export const setDateRangeType = (dateRangeType: string) => ({
  type: RevenueTrackingActionTypes.SET_DATE_RANGE_TYPE,
  payload: dateRangeType,
})

export const setFilters = (filters: FiltersState) => ({
  type: RevenueTrackingActionTypes.SET_FILTERS,
  payload: filters,
})

export const clearRevenueFilters = () => ({
  type: RevenueTrackingActionTypes.CLEAR_REVENUE_FILTERS,
})
